/**
 * EAHPConstructionBase - Industrial Orbital Assembly Station
 * Where mining ships from Mercury deliver materials and new satellites are assembled
 * High-fidelity mega-structure with docking bays, scaffolding, and active construction
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

interface EAHPConstructionBaseProps {
  position: [number, number, number];
  sunPosition?: [number, number, number];
  scale?: number;
}

export function EAHPConstructionBase({
  position,
  sunPosition = [-30, 0, 0],
  scale = 1
}: EAHPConstructionBaseProps) {
  const baseRef = useRef<THREE.Group>(null);
  const rotatingRingRef = useRef<THREE.Group>(null);
  const craneArmRef = useRef<THREE.Group>(null);
  const dockingLightsRef = useRef<THREE.Group>(null);
  const assemblyPlatformRef = useRef<THREE.Group>(null);

  const { isPaused, animationSpeed, isClipMode, view } = useStore();

  // Calculate rotation to face the Sun
  const sunFacingRotation = useMemo(() => {
    const pos = new THREE.Vector3(...position);
    const sun = new THREE.Vector3(...sunPosition);
    const direction = sun.clone().sub(pos).normalize();
    // Calculate angle on XZ plane to face sun
    return Math.atan2(direction.x, direction.z);
  }, [position, sunPosition]);

  // Animation
  useFrame((state) => {
    if (isPaused) return;

    const time = state.clock.elapsedTime * animationSpeed;

    // Rotate the central ring slowly
    if (rotatingRingRef.current) {
      rotatingRingRef.current.rotation.y = time * 0.1;
    }

    // Crane arm swings back and forth
    if (craneArmRef.current) {
      craneArmRef.current.rotation.y = Math.sin(time * 0.3) * 0.5;
    }

    // Assembly platform rotates opposite direction
    if (assemblyPlatformRef.current) {
      assemblyPlatformRef.current.rotation.y = -time * 0.05;
    }

    // Pulsing docking lights
    if (dockingLightsRef.current) {
      const pulse = 0.5 + Math.sin(time * 3) * 0.5;
      dockingLightsRef.current.children.forEach((child, i) => {
        if (child instanceof THREE.PointLight) {
          child.intensity = 2 + pulse * 3;
        }
      });
    }
  });

  return (
    <group ref={baseRef} position={position} scale={scale} rotation={[0, sunFacingRotation, 0]}>

      {/* ===== CENTRAL HUB - Main Station Core ===== */}
      <group>
        {/* Primary cylinder hub */}
        <mesh>
          <cylinderGeometry args={[0.8, 1.0, 2.5, 12]} />
          <meshStandardMaterial
            color={0x445566}
            metalness={0.8}
            roughness={0.3}
          />
        </mesh>

        {/* Hub detail rings */}
        {[-0.8, 0, 0.8].map((y, i) => (
          <mesh key={i} position={[0, y, 0]}>
            <torusGeometry args={[1.05, 0.08, 8, 24]} />
            <meshStandardMaterial
              color={0x667788}
              metalness={0.9}
              roughness={0.2}
            />
          </mesh>
        ))}

        {/* Central command module */}
        <mesh position={[0, 1.5, 0]}>
          <dodecahedronGeometry args={[0.5, 0]} />
          <meshStandardMaterial
            color={0x88aacc}
            metalness={0.7}
            roughness={0.2}
            emissive={0x224466}
            emissiveIntensity={0.5}
          />
        </mesh>

        {/* Command module windows */}
        {[0, Math.PI/3, Math.PI*2/3, Math.PI, Math.PI*4/3, Math.PI*5/3].map((angle, i) => (
          <mesh
            key={i}
            position={[
              Math.cos(angle) * 0.52,
              1.5,
              Math.sin(angle) * 0.52
            ]}
            rotation={[0, -angle, 0]}
          >
            <boxGeometry args={[0.15, 0.1, 0.02]} />
            <meshBasicMaterial
              color={0x00ffff}
              transparent
              opacity={0.9}
              toneMapped={false}
            />
          </mesh>
        ))}

        {/* Antenna array on top */}
        <group position={[0, 2, 0]}>
          <mesh>
            <cylinderGeometry args={[0.02, 0.02, 0.8, 6]} />
            <meshStandardMaterial color={0x888888} metalness={0.9} roughness={0.2} />
          </mesh>
          <mesh position={[0, 0.5, 0]}>
            <sphereGeometry args={[0.08, 8, 8]} />
            <meshBasicMaterial
              color={0xff0000}
              toneMapped={false}
            />
          </mesh>
          <pointLight position={[0, 0.5, 0]} color={0xff0000} intensity={1} distance={3} />
        </group>
      </group>

      {/* ===== ROTATING DOCKING RING ===== */}
      <group ref={rotatingRingRef} position={[0, 0, 0]}>
        {/* Main ring structure */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[2.5, 0.15, 8, 32]} />
          <meshStandardMaterial
            color={0x556677}
            metalness={0.8}
            roughness={0.3}
          />
        </mesh>

        {/* Docking ports - 6 around the ring */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i / 6) * Math.PI * 2;
          const x = Math.cos(angle) * 2.5;
          const z = Math.sin(angle) * 2.5;

          return (
            <group key={i} position={[x, 0, z]} rotation={[0, -angle + Math.PI/2, 0]}>
              {/* Docking arm */}
              <mesh position={[0.4, 0, 0]}>
                <boxGeometry args={[0.8, 0.3, 0.3]} />
                <meshStandardMaterial
                  color={0x445566}
                  metalness={0.7}
                  roughness={0.4}
                />
              </mesh>

              {/* Docking clamp */}
              <mesh position={[0.9, 0, 0]}>
                <boxGeometry args={[0.2, 0.5, 0.5]} />
                <meshStandardMaterial
                  color={0x334455}
                  metalness={0.8}
                  roughness={0.3}
                />
              </mesh>

              {/* Status light */}
              <mesh position={[0.9, 0.35, 0]}>
                <sphereGeometry args={[0.06, 8, 8]} />
                <meshBasicMaterial
                  color={i % 2 === 0 ? 0x00ff00 : 0xffaa00}
                  toneMapped={false}
                />
              </mesh>
              <pointLight
                position={[0.9, 0.35, 0]}
                color={i % 2 === 0 ? 0x00ff00 : 0xffaa00}
                intensity={0.5}
                distance={1.5}
              />
            </group>
          );
        })}
      </group>

      {/* ===== ASSEMBLY SCAFFOLDING ===== */}
      <group ref={assemblyPlatformRef} position={[0, -1.5, 0]}>
        {/* Scaffolding frame */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.8, 0.05, 6, 24]} />
          <meshStandardMaterial color={0xff6600} metalness={0.6} roughness={0.5} />
        </mesh>

        {/* Vertical supports */}
        {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
          <mesh
            key={i}
            position={[Math.cos(angle) * 1.8, 0, Math.sin(angle) * 1.8]}
          >
            <cylinderGeometry args={[0.04, 0.04, 1.5, 6]} />
            <meshStandardMaterial color={0xff6600} metalness={0.6} roughness={0.5} />
          </mesh>
        ))}

        {/* Satellite under construction (partial) */}
        <group position={[0, -0.3, 0]}>
          {/* Partial cone (heat shield being assembled) */}
          <mesh rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.4, 0.6, 8, 1, true, 0, Math.PI * 1.5]} />
            <meshStandardMaterial
              color={0xcccccc}
              metalness={0.9}
              roughness={0.1}
              side={THREE.DoubleSide}
            />
          </mesh>

          {/* Exposed framework */}
          {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
            <mesh
              key={i}
              position={[Math.cos(angle) * 0.25, -0.2, Math.sin(angle) * 0.25]}
              rotation={[0.3, angle, 0]}
            >
              <boxGeometry args={[0.02, 0.5, 0.02]} />
              <meshStandardMaterial color={0x666666} metalness={0.7} roughness={0.3} />
            </mesh>
          ))}

          {/* Assembly sparks/welding effect */}
          <pointLight position={[0.2, -0.1, 0.2]} color={0xffff00} intensity={2} distance={1} />
        </group>
      </group>

      {/* ===== CRANE ARM ===== */}
      <group ref={craneArmRef} position={[0, 0.5, 0]}>
        {/* Crane base */}
        <mesh>
          <cylinderGeometry args={[0.2, 0.25, 0.4, 8]} />
          <meshStandardMaterial color={0xffaa00} metalness={0.7} roughness={0.4} />
        </mesh>

        {/* Main arm */}
        <mesh position={[1.5, 0.3, 0]} rotation={[0, 0, -0.2]}>
          <boxGeometry args={[3, 0.15, 0.15]} />
          <meshStandardMaterial color={0xffaa00} metalness={0.7} roughness={0.4} />
        </mesh>

        {/* Crane hook assembly */}
        <group position={[2.8, 0, 0]}>
          <mesh>
            <cylinderGeometry args={[0.05, 0.05, 0.6, 6]} />
            <meshStandardMaterial color={0x888888} metalness={0.8} roughness={0.3} />
          </mesh>
          <mesh position={[0, -0.4, 0]}>
            <torusGeometry args={[0.12, 0.03, 8, 12]} />
            <meshStandardMaterial color={0xaaaaaa} metalness={0.9} roughness={0.2} />
          </mesh>
        </group>
      </group>

      {/* ===== CARGO STORAGE MODULES ===== */}
      {[
        { pos: [1.5, -0.5, 1.5], rot: 0.3 },
        { pos: [-1.5, -0.3, 1.2], rot: -0.5 },
        { pos: [1.2, -0.4, -1.5], rot: 0.8 },
      ].map((cargo, i) => (
        <group key={i} position={cargo.pos as [number, number, number]} rotation={[0, cargo.rot, 0]}>
          {/* Container */}
          <mesh>
            <boxGeometry args={[0.6, 0.4, 0.3]} />
            <meshStandardMaterial
              color={i === 0 ? 0x4488ff : i === 1 ? 0xff8844 : 0x44ff88}
              metalness={0.5}
              roughness={0.5}
            />
          </mesh>
          {/* Container markings */}
          <mesh position={[0, 0, 0.16]}>
            <planeGeometry args={[0.4, 0.25]} />
            <meshBasicMaterial color={0x222222} />
          </mesh>
        </group>
      ))}

      {/* ===== SOLAR POWER ARRAYS ===== */}
      {[
        { angle: Math.PI / 4, dist: 2 },
        { angle: -Math.PI / 4 + Math.PI, dist: 2 },
      ].map((panel, i) => (
        <group
          key={i}
          position={[
            Math.cos(panel.angle) * panel.dist,
            0.5,
            Math.sin(panel.angle) * panel.dist
          ]}
          rotation={[0, -panel.angle, 0]}
        >
          {/* Panel mount */}
          <mesh>
            <boxGeometry args={[0.1, 0.1, 0.5]} />
            <meshStandardMaterial color={0x666666} metalness={0.7} roughness={0.4} />
          </mesh>
          {/* Solar panel */}
          <mesh position={[0, 0, 0.4]} rotation={[0.3, 0, 0]}>
            <boxGeometry args={[1.2, 0.02, 0.6]} />
            <meshStandardMaterial
              color={0x2244aa}
              metalness={0.3}
              roughness={0.6}
              emissive={0x112244}
              emissiveIntensity={0.3}
            />
          </mesh>
        </group>
      ))}

      {/* ===== DOCKING GUIDE LIGHTS ===== */}
      <group ref={dockingLightsRef}>
        {/* Approach beacon lights */}
        {[3, 4, 5, 6].map((dist, i) => (
          <group key={i} position={[dist, 0, 0]}>
            <mesh>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshBasicMaterial
                color={0x00ff00}
                transparent
                opacity={0.9}
                toneMapped={false}
              />
            </mesh>
            <pointLight color={0x00ff00} intensity={2} distance={2} />
          </group>
        ))}

        {/* Warning strobes */}
        {[
          [2, 1.5, 0],
          [-2, 1.5, 0],
          [0, 1.5, 2],
          [0, 1.5, -2],
        ].map((pos, i) => (
          <group key={i} position={pos as [number, number, number]}>
            <mesh>
              <octahedronGeometry args={[0.08, 0]} />
              <meshBasicMaterial
                color={0xffaa00}
                toneMapped={false}
              />
            </mesh>
            <pointLight color={0xffaa00} intensity={1} distance={3} />
          </group>
        ))}
      </group>

      {/* ===== COMMUNICATION DISHES ===== */}
      <group position={[0, 2.2, 0.8]} rotation={[0.5, 0, 0]}>
        <mesh>
          <cylinderGeometry args={[0.01, 0.01, 0.3, 6]} />
          <meshStandardMaterial color={0x888888} metalness={0.8} roughness={0.3} />
        </mesh>
        <mesh position={[0, 0.2, 0]} rotation={[Math.PI/2, 0, 0]}>
          <coneGeometry args={[0.25, 0.15, 16, 1, true]} />
          <meshStandardMaterial
            color={0xaaaaaa}
            metalness={0.9}
            roughness={0.1}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>

      {/* ===== AMBIENT GLOW ===== */}
      <mesh>
        <sphereGeometry args={[3.5, 16, 16]} />
        <meshBasicMaterial
          color={0x4488ff}
          transparent
          opacity={0.03}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>

      {/* Main station lights */}
      <pointLight position={[0, 0, 0]} color={0xaaccff} intensity={3} distance={8} />
      <pointLight position={[0, -2, 0]} color={0xff6600} intensity={2} distance={5} />

      {/* ===== STATION LABEL - hidden during clip mode ===== */}
      {view.showLabels && !isClipMode && (
        <sprite position={[0, 3.5, 0]} scale={[3, 0.75, 1]}>
          <spriteMaterial
            map={createStationLabel()}
            transparent
            opacity={0.95}
          />
        </sprite>
      )}
    </group>
  );
}

// Station label texture
function createStationLabel(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = 'rgba(0, 20, 40, 0.9)';
  ctx.fillRect(0, 0, 512, 128);

  ctx.strokeStyle = '#00aaff';
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, 508, 124);

  ctx.fillStyle = '#00ddff';
  ctx.font = 'bold 28px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('EAHP ASSEMBLY STATION', 256, 45);

  ctx.fillStyle = '#0099cc';
  ctx.font = '16px monospace';
  ctx.fillText('CONSTRUCTION & DEPLOYMENT HUB', 256, 85);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
